/**
 * src/types/charts/funnel.chart.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Funnel chart — conversion funnel with stage-by-stage breakdown.
 * Segments taper from widest (first stage) to narrowest (last stage).
 * `showPercentage: true` renders the conversion rate between consecutive stages.
 *
 * FUTURE: renderer not yet implemented — types are production-ready.
 */

import type { ChartDataPoint } from '../core/interfaces.shared';
import {
  makeTitle, makeSubtitle,
  DEFAULT_BACKGROUND, DEFAULT_LEGEND, DEFAULT_DATA_LABELS,
} from '../core/defaults.shared';
import type { ChartConfigBase } from '../core/chart-config.base';

// ─────────────────────────────────────────────────────────────────────────────
// ANIMATION STYLES
// ─────────────────────────────────────────────────────────────────────────────

export type FunnelAnimationStyle = 'classic' | 'sequential';

export const FUNNEL_ANIMATION_STYLES: FunnelAnimationStyle[] = ['classic', 'sequential'];

export const FUNNEL_ELEMENT_PANEL_STYLES: FunnelAnimationStyle[] = [];


// ─────────────────────────────────────────────────────────────────────────────
// DATA POINT
// ─────────────────────────────────────────────────────────────────────────────

export interface FunnelDataPoint extends ChartDataPoint {
  chartType: 'funnel';
  value:     number;   // stage size — count or conversion rate
}


// ─────────────────────────────────────────────────────────────────────────────
// SPACING CONFIG
// ─────────────────────────────────────────────────────────────────────────────

export interface FunnelSpacingConfig {
  chartType:      'funnel';
  segmentHeight:  number;    // px height of each funnel segment. Range: 20–300. Default: 100.
  segmentGap:     number;    // px gap between segments. Range: 0–40. Default: 8.
  showPercentage: boolean;   // render conversion % between stages. Default: true.
}


// ─────────────────────────────────────────────────────────────────────────────
// DEFAULTS
// ─────────────────────────────────────────────────────────────────────────────

export const FUNNEL_DEFAULTS: ChartConfigBase<FunnelSpacingConfig> = {
  chartType: 'funnel',
  content: {
    titles:    [makeTitle('Conversion Funnel')],
    subtitles: [makeSubtitle('Stage-by-Stage Breakdown')],
    infoBoxes: [],
  },
  animation:  { duration: 5, style: 'sequential', textSpeed: 1 },
  background: { ...DEFAULT_BACKGROUND },
  layout: {
    chartPosition: { x: 0, y: 0 },
    chartMargin:   { top: 180, right: 80, bottom: 80, left: 80 },
  },
  // No axes.
  legend:     { ...DEFAULT_LEGEND },
  dataLabels: { ...DEFAULT_DATA_LABELS },
  spacing: {
    chartType:      'funnel',
    segmentHeight:  100,
    segmentGap:     8,
    showPercentage: true,
  },
  export: { quality: '1080p', isLooping: false, canvasFormat: '16:9' },
};
